<template>
  <v-container fluid>
    <v-row align="center">
      <v-col cols="auto">
        <span class="subtitle-1">{{ $t('scaleSheetProfiles') }}</span>
      </v-col>
      <v-col>
        <Icon
          icon="mdi-plus"
          iconColor="primary"
          :small="false"
          :tooltipText="$t('addScaleSheetProfile')"
          @icon-clicked="openCreateDialog()"
        />
        <Icon
          icon="mdi-import"
          class="ml-1"
          iconColor="primary"
          :small="false"
          :tooltipText="$t('importScaleSheetProfile')"
          @icon-clicked="triggerFileInput()"
        />
        <v-file-input
          v-model="importFiles"
          accept="application/json,.json"
          style="display: none"
          @change="onImportFileSelected(importFiles)"
          hide-details
          multiple
        />
      </v-col>
    </v-row>
    <v-row v-if="missingItems.length > 0">
      <v-col cols="12">
        <v-alert
          class="mb-0 pb-6"
          color="red"
          elevation="4"
          outlined
          text
          dismissible
        >
          <div>{{ $t('profileImportsFailed') }}:</div>
          <v-row align="stretch">
            <v-col
              v-for="(fileError, idx) in missingItems"
              :key="fileError.fileName + idx"
              cols="12"
              md="6"
              lg="4"
            >
              <v-card class="mt-2 fill-height d-flex flex-column">
                <v-card-title class="error">
                  <span class="subtitle-2 white--text">{{ fileError.fileName }}</span>
                </v-card-title>
                <v-card-text class="flex-grow-1">
                  <ul class="mt-4">
                    <li v-for="(item, i) in fileError.items" :key="i">{{ item }}</li>
                  </ul>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-alert>
      </v-col>
    </v-row>
    <v-row v-if="scaleSheetProfiles.length > 0 && !pageLoading">
      <v-col v-for="(profile, index) in scaleSheetProfiles" :key="index" cols="12" md="4" lg="2">
        <v-card outlined rounded class="fill-height" style="position: relative;">
          <Icon
            v-if="profile.scaleSheetImportDelimitedProfile && profile.scaleSheetImportDelimitedProfile.virtualPrefix"
            icon="mdi-alpha-v-circle-outline"
            iconColor="primary"
            :small="false"
            :tooltipText="$t('usesVirtualPrefix')"
            margin=""
            style="position: absolute; bottom: 4px; right: 4px;"
          />
          <v-card-title>
            <v-row justify="space-between" no-gutters style="width: 100%;">
              <v-col class="mr-2 text-truncate">
                <span class="subtitle-1">
                  {{ profile.name }}
                </span>
              </v-col>
              <v-col cols="auto">
                <Icon
                  icon="mdi-information-outline"
                  iconColor="primary"
                  :tooltipText="$t('showDetails')"
                  :small="false"
                  @icon-clicked="openScaleSheetDetail(profile)"
                />
                <Icon
                  icon="mdi-pencil"
                  :tooltipText="$t('edit')"
                  iconColor="success"
                  @icon-clicked="editScaleSheetProfile(profile)"
                />
                <Icon
                  icon="mdi-delete"
                  iconColor="error"
                  :tooltipText="$t('delete')"
                  @icon-clicked="openDeleteDialog(profile)"
                />
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text class="mt-n2">
            <v-row dense>
              <v-col cols="12" class="text-truncate">{{ $t('fileType') }}: {{ getFileTypeString(profile.scaleSheetImportFileType) }}</v-col>
              <v-col cols="12" v-if="uniqueProfileSpecies(profile).length > 0">
                <v-row align="center" dense>
                  <v-col cols="auto">
                    {{ $t('species') }}:
                  </v-col>
                  <v-col class="text-truncate">
                    <v-tooltip bottom color="black" v-if="uniqueProfileSpecies(profile).length > 1">
                      <template #activator="{on}">
                        <v-icon v-on="on" color="black">
                          mdi-dots-horizontal
                        </v-icon>
                      </template>
                      <v-col v-for="(speciesProfile, index) in uniqueProfileSpecies(profile)" :key="index + ' sp'">
                        <span class="white--text subtitle-1">
                          {{ speciesProfile.species.code }}
                        </span>
                      </v-col>
                    </v-tooltip>
                    <span v-else>
                      {{ profile.speciesProfiles[0].species.code }}
                    </span>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" v-if="uniqueProfileGrades(profile).length > 0">
                <v-row align="center" dense>
                  <v-col cols="auto">
                    {{ $t('grades') }}:
                  </v-col>
                  <v-col class="text-truncate">
                    <v-tooltip bottom color="black" v-if="uniqueProfileGrades(profile).length > 1">
                      <template #activator="{on}">
                        <v-icon v-on="on" color="black">
                          mdi-dots-horizontal
                        </v-icon>
                      </template>
                      <v-col v-for="(gradeProfile, index) in uniqueProfileGrades(profile)" :key="index + ' gp'">
                        <span class="white--text subtitle-1">
                          {{ gradeProfile.grade.code }}
                        </span>
                      </v-col>
                    </v-tooltip>
                    <span v-else>
                      {{ profile.gradeProfiles[0].grade.code }}
                    </span>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" v-if="uniqueProfileEndUseSorts(profile).length > 0">
                <v-row align="center" dense>
                  <v-col cols="auto">
                    {{ $t('endUseSorts') }}:
                  </v-col>
                  <v-col class="text-truncate">
                    <v-tooltip bottom color="black" v-if="uniqueProfileEndUseSorts(profile).length > 1">
                      <template #activator="{on}">
                        <v-icon v-on="on" color="black">
                          mdi-dots-horizontal
                        </v-icon>
                      </template>
                      <v-col v-for="(endUseSortProfile, index) in uniqueProfileEndUseSorts(profile)" :key="index + ' eus'">
                        <span class="white--text subtitle-1">
                          {{ endUseSortProfile.endUseSort.name }}
                        </span>
                      </v-col>
                    </v-tooltip>
                    <span v-else>
                      {{ profile.endUseSortProfiles[0].endUseSort.name }}
                    </span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="!pageLoading && scaleSheetProfiles.length === 0">
      <v-col cols="auto">
        <NoData>{{ $t('noScaleSheetProfiles') }}</NoData>
      </v-col>
    </v-row>
    <v-row v-if="pageLoading">
      <v-col cols="12">
        <v-progress-linear rounded indeterminate class="mb-n4"/>
      </v-col>
    </v-row>
    <Dialog :stateId="dialogId" maxWidth="80vw" @close="resetDialogs">
      <ScaleSheetForm
        v-if="formDialogOpen"
        :propProfile="selectedProfile"
        :propSpecies="allSpecies"
        :propGrades="allGrades"
        :propEndUseSorts="allEndUseSorts"
        :propAccounts="allAccounts"
        :propSettings="allSettings"
        :propTracts="filteredTracts"
        :propProducts="allProducts"
        @close="resetDialogs"
        @save="saveScaleSheetProfile"
      />
      <ScaleSheetDetail
        v-if="detailDialogOpen"
        :propProfile="selectedProfile"
        :propGrades="allGrades"
        :propAccounts="allAccounts"
        :propSettings="allSettings"
        :propTracts="filteredTracts"
        :propProducts="allProducts"
        @edit-scale-sheet-profile="editScaleSheetProfile"
        @export-scale-sheet-profile="exportScaleSheetProfile"
        @close="resetDialogs"
      />
      <ConfirmDialog
        v-if="deleteDialogOpen"
        :title="$t('deleteScaleSheetProfileTitle')"
        :body="$t('deleteScaleSheetProfileBody')"
        @confirm="deleteScaleSheetProfile"
        @cancel="resetDialogs"
      />
    </Dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { ScaleSheetImportFileType, TractStatus } from '@/utils/Enumerations'
import { TractFilter } from '@/model/Tract.js'
import { saveAs } from 'file-saver'

export default {
  name: 'ScaleSheetProfiles',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    ScaleSheetForm: () => import('@/components/settings-components/imports/scale-sheet-import-components/ScaleSheetForm.vue'),
    ScaleSheetDetail: () => import('@/components/settings-components/imports/scale-sheet-import-components/ScaleSheetDetail.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    NoData: () => import('@/components/core/NoData.vue')
  },

  data: () => ({
    dialogId: uniqueDialogId('scale-sheet-profile'),
    formDialogOpen: false,
    deleteDialogOpen: false,
    detailDialogOpen: false,
    selectedProfile: undefined,
    importFiles: [],
    missingItems: []
  }),

  async created () {
    await this.fetchProfiles()
    await this.fetchSpecies()
    await this.fetchGrades()
    await this.fetchEndUseSorts()
    await this.fetchAccounts()
    await this.fetchSettings()
    await this.refreshTracts()
    await this.fetchProducts()
  },

  computed: {
    ...mapGetters('scale-sheet', ['scaleSheetProfiles', 'loading']),
    ...mapGetters('species', ['allSpecies', 'speciesLoading']),
    ...mapGetters('grade', ['allGrades', 'gradesLoading']),
    ...mapGetters('end-use-sort', ['allEndUseSorts', 'endUseSortsLoading']),
    ...mapGetters('account', ['allAccounts', 'accountsLoading']),
    ...mapGetters('setting', ['allSettings', 'settingsLoading']),
    ...mapGetters('tract', ['allTracts', 'tractsLoading']),
    ...mapGetters('product', ['allProducts', 'productsLoading']),

    pageLoading () {
      return this.loading || this.speciesLoading || this.gradesLoading || this.endUseSortsLoading || this.accountsLoading || this.settingsLoading || this.tractsLoading || this.productsLoading
    },

    filteredTracts () {
      return this.allTracts.filter(t => t.status === TractStatus.Active.value ||
      (t.status === TractStatus.Archived.value && this.selectedProfile?.tractProfiles?.some(tp => tp.tractId === t.tractId)) ||
      (t.status === TractStatus.Closed.value && this.selectedProfile?.tractProfiles?.some(tp => tp.tractId === t.tractId)) ||
      (t.status === TractStatus.Executed.value && this.selectedProfile?.tractProfiles?.some(tp => tp.tractId === t.tractId)))
    }
  },

  methods: {
    ...mapActions('scale-sheet', ['fetchProfiles', 'createProfile', 'updateProfile', 'deleteProfile']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('species', ['fetchSpecies']),
    ...mapActions('grade', ['fetchGrades']),
    ...mapActions('end-use-sort', ['fetchEndUseSorts']),
    ...mapActions('account', ['fetchAccounts']),
    ...mapActions('setting', ['fetchSettings']),
    ...mapActions('tract', ['fetchTracts']),
    ...mapActions('product', ['fetchProducts']),

    openScaleSheetDetail (profile) {
      this.selectedProfile = profile
      this.detailDialogOpen = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
    },

    editScaleSheetProfile (profile) {
      this.selectedProfile = profile
      this.detailDialogOpen = false
      this.formDialogOpen = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
    },

    openDeleteDialog (profile) {
      this.selectedProfile = profile
      this.deleteDialogOpen = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '400' })
    },

    openCreateDialog () {
      this.resetDialogs()
      this.formDialogOpen = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
    },

    resetDialogs () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.formDialogOpen = false
      this.deleteDialogOpen = false
      this.detailDialogOpen = false
      this.selectedProfile = undefined
    },

    getFileTypeString (fileType) {
      return ScaleSheetImportFileType.fromInt(fileType)
    },

    async saveScaleSheetProfile (profile) {
      if (profile.scaleSheetImportProfileId) {
        await this.updateProfile(profile)
      } else {
        await this.createProfile(profile)
      }
      this.resetDialogs()
      await this.fetchProfiles()
    },

    async deleteScaleSheetProfile () {
      this.closeDialogsAtOrAbove(this.dialogId)
      await this.deleteProfile(this.selectedProfile?.scaleSheetImportProfileId)
      await this.fetchProfiles()
      this.resetDialogs()
    },

    async refreshTracts () {
      const tractFilter = new TractFilter()
      tractFilter.includeActive = true
      tractFilter.includeArchived = true
      tractFilter.includeClosed = true
      tractFilter.includeExecuted = true
      await this.fetchTracts(tractFilter)
    },

    uniqueProfileSpecies (profile) {
      const grouped = profile?.speciesProfiles?.reduce((acc, sp) => {
        if (!acc.find(s => s.speciesId === sp.speciesId)) {
          acc.push(sp)
        }
        return acc
      }, [])
      return grouped
    },

    uniqueProfileGrades (profile) {
      const grouped = profile?.gradeProfiles?.reduce((acc, gp) => {
        if (!acc.find(g => g.gradeId === gp.gradeId)) {
          acc.push(gp)
        }
        return acc
      }, [])
      return grouped
    },

    uniqueProfileEndUseSorts (profile) {
      const grouped = profile?.endUseSortProfiles?.reduce((acc, eup) => {
        if (!acc.find(eu => eu.endUseSortId === eup.endUseSortId)) {
          acc.push(eup)
        }
        return acc
      }, [])
      return grouped
    },

    exportScaleSheetProfile (profile) {
      const getAccountName = id => this.allAccounts.find(a => a.accountId === id)?.name || ''
      const getSpeciesName = id => this.allSpecies.find(s => s.speciesId === id)?.commonName || ''
      const getGradeName = id => this.allGrades.find(g => g.gradeId === id)?.name || ''
      const getEndUseSortName = id => this.allEndUseSorts.find(e => e.endUseSortId === id)?.name || ''
      const getSettingName = id => this.allSettings.find(s => s.settingId === id)?.name || ''
      const getTractName = id => this.allTracts.find(t => t.tractId === id)?.name || ''
      const getProductName = id => this.allProducts.find(p => p.productId === id)?.name || ''

      const exportProfile = {
        name: profile.name,
        scaleSheetImportFileType: profile.scaleSheetImportFileType,
        scaleSheetImportCsvProfile: profile.scaleSheetImportCsvProfile,
        scaleSheetImportDelimitedProfile: profile.scaleSheetImportDelimitedProfile,
        scaleSheetImportFixedLengthProfile: profile.scaleSheetImportFixedLengthProfile,
        defaultInWeight: profile.defaultInWeight,
        defaultOutWeight: profile.defaultOutWeight,
        buttLogIndicator: profile.buttLogIndicator,
        multiplicationFactor: profile.multiplicationFactor,
        defaultGrade: profile.defaultGrade?.name || '',
        defaultSetting: profile.defaultSettingId ? getSettingName(profile.defaultSettingId) : '',
        defaultTrailerIdentifier: profile.defaultTrailerIdentifier || null,
        speciesProfiles: profile.speciesProfiles?.map(sp => ({
          externalSpeciesCode: sp.externalSpeciesCode,
          speciesCommonName: getSpeciesName(sp.speciesId)
        })),
        gradeProfiles: profile.gradeProfiles?.map(gp => ({
          externalGradeCode: gp.externalGradeCode,
          gradeName: getGradeName(gp.gradeId)
        })),
        endUseSortProfiles: profile.endUseSortProfiles?.map(eup => ({
          externalEndUseSortCode: eup.externalEndUseSortCode,
          endUseSortName: getEndUseSortName(eup.endUseSortId)
        })),
        accountProfiles: profile.accountProfiles?.map(ap => ({
          externalAccountName: ap.externalAccountName,
          accountName: getAccountName(ap.accountId)
        })),
        settingProfiles: profile.settingProfiles?.map(sp => ({
          externalSettingName: sp.externalSettingName,
          settingName: getSettingName(sp.settingId)
        })),
        tractProfiles: profile.tractProfiles?.map(tp => ({
          externalTractName: tp.externalTractName,
          tractName: getTractName(tp.tractId)
        })),
        productProfiles: profile.productProfiles?.map(pp => ({
          externalProductName: pp.externalProductName,
          productName: getProductName(pp.productId)
        }))
      }

      const blob = new Blob([JSON.stringify(exportProfile, null, 2)], { type: 'application/json' })
      saveAs(blob, `${profile.name} - ScaleSheetProfile.json`)
    },

    triggerFileInput () {
      this.$el.querySelector('input[type="file"]').click()
    },

    async onImportFileSelected (files) {
      this.missingItems = []
      const fileList = Array.isArray(files) ? files : [files]
      if (!fileList.length) return

      const findSpeciesId = name => this.allSpecies.find(s => s.commonName === name)?.speciesId || null
      const findGradeId = name => this.allGrades.find(g => g.name === name)?.gradeId || null
      const findEndUseSortId = name => this.allEndUseSorts.find(e => e.name === name)?.endUseSortId || null
      const findAccountId = name => this.allAccounts.find(a => a.name === name)?.accountId || null
      const findSettingId = name => this.allSettings.find(s => s.name === name)?.settingId || null
      const findTractId = name => this.allTracts.find(t => t.name === name)?.tractId || null
      const findProductId = name => this.allProducts.find(p => p.name === name)?.productId || null

      const profileTypes = [
        { key: 'speciesProfiles', nameField: 'speciesCommonName', finder: findSpeciesId, label: 'Species' },
        { key: 'gradeProfiles', nameField: 'gradeName', finder: findGradeId, label: 'Grade' },
        { key: 'endUseSortProfiles', nameField: 'endUseSortName', finder: findEndUseSortId, label: 'End Use Sort' },
        { key: 'accountProfiles', nameField: 'accountName', finder: findAccountId, label: 'Account' },
        { key: 'settingProfiles', nameField: 'settingName', finder: findSettingId, label: 'Setting' },
        { key: 'tractProfiles', nameField: 'tractName', finder: findTractId, label: 'Tract' },
        { key: 'productProfiles', nameField: 'productName', finder: findProductId, label: 'Product' }
      ]

      for (const file of fileList) {
        try {
          const text = await file.text()
          const imported = JSON.parse(text)
          const missingItems = []

          if (imported.defaultGrade && !findGradeId(imported.defaultGrade)) {
            missingItems.push(this.$t('missingItemInScaleSheetProfile', { profileLabel: this.$t('defaultGrade'), missingItem: imported.defaultGrade }))
          }

          if (imported.defaultSetting && !findSettingId(imported.defaultSetting)) {
            missingItems.push(this.$t('missingItemInScaleSheetProfile', { profileLabel: this.$t('defaultSetting'), missingItem: imported.defaultSetting }))
          }

          profileTypes.forEach(type => {
            (imported[type.key] || []).forEach(item => {
              if (!type.finder(item[type.nameField])) {
                missingItems.push(this.$t('missingItemInScaleSheetProfile', { profileLabel: this.$t(type.label), missingItem: item[type.nameField] }))
              }
            })
          })

          if (missingItems.length > 0) {
            this.missingItems.push({ fileName: file.name, items: missingItems })
            continue
          }

          const profile = {
            name: imported.name,
            scaleSheetImportProfileId: 0,
            scaleSheetImportFileType: imported.scaleSheetImportFileType,
            scaleSheetImportCsvProfile: imported.scaleSheetImportCsvProfile,
            scaleSheetImportDelimitedProfile: imported.scaleSheetImportDelimitedProfile,
            scaleSheetImportFixedLengthProfile: imported.scaleSheetImportFixedLengthProfile,
            defaultInWeight: imported.defaultInWeight?.toString() ?? null,
            defaultOutWeight: imported.defaultOutWeight?.toString() ?? null,
            buttLogIndicator: imported.buttLogIndicator,
            multiplicationFactor: imported.multiplicationFactor?.toString() ?? null,
            defaultGradeId: findGradeId(imported.defaultGrade),
            defaultGrade: null,
            defaultSettingId: findSettingId(imported.defaultSetting),
            defaultSetting: null,
            defaultTrailerIdentifier: imported.defaultTrailerIdentifier || null,
            speciesProfiles: (imported.speciesProfiles || []).map(sp => ({
              externalSpeciesCode: sp.externalSpeciesCode,
              speciesId: findSpeciesId(sp.speciesCommonName),
              scaleSheetImportProfileId: 0
            })),
            gradeProfiles: (imported.gradeProfiles || []).map(gp => ({
              externalGradeCode: gp.externalGradeCode,
              gradeId: findGradeId(gp.gradeName),
              scaleSheetImportProfileId: 0
            })),
            endUseSortProfiles: (imported.endUseSortProfiles || []).map(eup => ({
              externalEndUseSortCode: eup.externalEndUseSortCode,
              endUseSortId: findEndUseSortId(eup.endUseSortName),
              scaleSheetImportProfileId: 0
            })),
            accountProfiles: (imported.accountProfiles || []).map(ap => ({
              externalAccountName: ap.externalAccountName,
              accountId: findAccountId(ap.accountName),
              scaleSheetImportProfileId: 0
            })),
            settingProfiles: (imported.settingProfiles || []).map(sp => ({
              externalSettingName: sp.externalSettingName,
              settingId: findSettingId(sp.settingName),
              scaleSheetImportProfileId: 0
            })),
            tractProfiles: (imported.tractProfiles || []).map(tp => ({
              externalTractName: tp.externalTractName,
              tractId: findTractId(tp.tractName),
              scaleSheetImportProfileId: 0
            })),
            productProfiles: (imported.productProfiles || []).map(pp => ({
              externalProductName: pp.externalProductName,
              productId: findProductId(pp.productName),
              scaleSheetImportProfileId: 0
            }))
          }

          await this.createProfile(profile)
          this.setSnack(this.$t('profileImportSuccess'))
          await this.fetchProfiles()
        } catch (e) {
          this.setSnackError(this.$t('errorImportingScaleSheetProfile', { code: e.response?.data.code || console.error(e) }))
        }
      }

      // Reset the file input so the same file can be selected again
      this.importFiles = []
    }
  }
}
</script>
